// SignIn.js
import React, { useEffect, useRef } from "react";
import {
  signInWithPopup,
  signInAnonymously,
  linkWithPopup,
} from "firebase/auth";
import { auth, googleProvider, facebookProvider } from "./firebase";
import "./SignIn.css"; 

const SignIn = ({ onSignIn, onClose }) => {
  const dialogRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prevent background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close when clicking the backdrop
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

const signInWithProvider = async (provider) => {
    try {
      const current = auth.currentUser;

      if (current && current.isAnonymous) {
        try {
          // 🌟 1. THE UPGRADE: Try to link the guest session to the new account
          const result = await linkWithPopup(current, provider);
          onSignIn(result.user); // App.js will update state & close modal
          
        } catch (linkError) {
          // 🚨 2. THE FALLBACK: Catch the conflict if the Google/Facebook account already exists
          if (linkError?.code === "auth/credential-already-in-use") {
            console.log("Account already registered. Switching from Guest to Existing Account...");
            
            // Abandon the guest ghost session
            await auth.signOut();
            
            // Sign them into their permanent account
            const existingResult = await signInWithPopup(auth, provider);
            onSignIn(existingResult.user);
          } else {
            throw linkError; // Throw other random errors (like closed popups) to the outer catch
          }
        }
      } else {
        // 3. NORMAL LOGIN: For users who are already fully logged out
        const result = await signInWithPopup(auth, provider);
        onSignIn(result.user);
      }
    } catch (error) {
      // Ignore if user just closes the popup
      if (error?.code === "auth/popup-closed-by-user") return;

      console.error("Sign-in error:", error);
    }
  };

  const handleGoogleSignIn = () => signInWithProvider(googleProvider);
  const handleFacebookSignIn = () => signInWithProvider(facebookProvider);

  const handleGuestSignIn = async () => {
    try {
      if (!auth.currentUser) {
        const result = await signInAnonymously(auth);
        onSignIn(result.user);
      } else {
        onClose?.();
      }
    } catch (error) {
      console.error("Error during guest sign-in:", error);
    }
  };

  return (
    <div
      className="signin-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onBackdropClick}
    >
      <div className="signin-glass-card" ref={dialogRef}>
        
        {/* Logo & Header */}
        <div className="signin-header">
          <img src="/signin.png" alt="Talkvee" className="signin-logo-img" />
          <h2>Unlock the World</h2>
          <p>Join the community to start matching.</p>
        </div>

        {/* Actions */}
        <div className="signin-actions">
          {/* Google */}
          <button className="signin-btn google" onClick={handleGoogleSignIn}>
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="G"
            />
            <span>Continue with Google</span>
          </button>

          {/* Facebook */}
          <button className="signin-btn facebook" onClick={handleFacebookSignIn}>
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/b/b8/2021_Facebook_icon.svg"
              alt="F"
            />
            <span>Continue with Facebook</span>
          </button>

          {/* Divider */}
          <div className="divider">
            <span>or</span>
          </div>

          {/* Guest */}
          <button className="signin-btn guest" onClick={handleGuestSignIn}>
            Continue as Guest
          </button>
        </div>

        {/* Footer (Updated with 18+) */}
        <div className="signin-footer">
          <small>
            By continuing, you confirm you are <strong>18+</strong> and agree to our <a href="/terms">Terms</a> & <a href="/privacy">Privacy Policy</a>.
          </small>
        </div>
      </div>
    </div>
  );
};

export default SignIn;